import { useScrollReveal } from '../hooks/useScrollReveal';
import { useEffect, useState } from 'react';

const flowSteps = [
  { id: '01', title: '课堂材料', desc: '视频/音频/课件/文本\n多源材料输入', icon: 'ri-folder-video-line', color: 'primary' as const },
  { id: '02', title: '多模态识别', desc: '文字、声音、画面\n多模态理解', icon: 'ri-file-music-line', color: 'primary' as const },
  { id: '03', title: '指标映射', desc: '关键片段对应\n评价维度', icon: 'ri-map-pin-2-line', color: 'primary' as const },
  { id: '04', title: '专用模型评价', desc: '训练后的思政课\n专用评价模型', icon: 'ri-brain-line', color: 'accent' as const },
  { id: '05', title: '证据链', desc: '原话、时间戳、\n课件页、视频片段', icon: 'ri-links-line', color: 'primary' as const },
  { id: '06', title: '总分 / 维度分 / 分档结果', desc: '结构化结果输出\n可视化呈现', icon: 'ri-bar-chart-grouped-line', color: 'accent' as const },
  { id: '07', title: '改进建议', desc: '面向教师的\n具体优化建议', icon: 'ri-lightbulb-line', color: 'primary' as const },
];

const radarData = [
  { label: '内容与结构', score: 88, short: '内容与结构', angle: 270 },
  { label: '价值引导', score: 90, short: '价值引导', angle: 342 },
  { label: '互动与表达', score: 82, short: '互动与表达', angle: 54 },
  { label: '课堂节奏', score: 83, short: '课堂节奏', angle: 126 },
  { label: '资料与资源', score: 80, short: '资料与资源', angle: 198 },
];

const radarScores = [
  { label: '内容与结构', score: 88 },
  { label: '互动与表达', score: 83 },
  { label: '课堂节奏', score: 82 },
  { label: '价值引导', score: 90 },
  { label: '资料与资源', score: 80 },
];

const viewerRoles = [
  {
    role: '管理者',
    icon: 'ri-building-2-line',
    desc: '看结构化结果，把握整体水平，\n支持决策管理',
    decor: 'chart',
    color: 'primary' as const,
  },
  {
    role: '专家',
    icon: 'ri-user-star-line',
    desc: '看评价依据，核验证据充分，\n支持专业判断',
    decor: 'checklist',
    color: 'accent' as const,
  },
  {
    role: '教师',
    icon: 'ri-graduation-cap-line',
    desc: '看具体改进点，聚焦改进方向，\n促进教学提升',
    decor: 'progress',
    color: 'secondary' as const,
  },
  {
    role: '学生',
    icon: 'ri-user-line',
    desc: '看学习收获点，理解课堂价值，\n促进自我成长',
    decor: 'book',
    color: 'primary' as const,
  },
];

const evidenceTabs = ['原话', '时间戳', '课件页', '视频片段', '评价维度', '改进建议'];

const evidenceData: Record<string, { time: string; text: string; type: string; tags: string[] }> = {
  '视频片段': {
    time: '08:35 - 08:42',
    text: '我们要坚持人民至上，这是中国共产党的根本立场。',
    type: '视频片段',
    tags: ['内容与结构', '价值引导'],
  },
  '原话': {
    time: '08:35',
    text: '"我们要坚持人民至上，这是中国共产党的根本立场。"',
    type: '原话引用',
    tags: ['价值引导'],
  },
  '时间戳': {
    time: '08:35 - 08:42',
    text: '教师结合案例阐述"人民至上"理念，学生专注听讲。',
    type: '时间戳记录',
    tags: ['内容与结构'],
  },
  '课件页': {
    time: '第12页',
    text: '课件展示"人民至上"相关论述，配数据图表说明。',
    type: '课件内容',
    tags: ['资料与资源'],
  },
  '评价维度': {
    time: '-',
    text: '价值引导维度得分90分，属于课程核心优势维度。',
    type: '维度评价',
    tags: ['价值引导'],
  },
  '改进建议': {
    time: '-',
    text: '可进一步结合具体案例，深化学生对"人民至上"内涵的理解与认同。',
    type: '改进建议',
    tags: ['互动与表达'],
  },
};

function RadarChart() {
  const cx = 70;
  const cy = 60;
  const r = 45;
  const levels = [0.2, 0.4, 0.6, 0.8, 1.0];

  const getPoint = (angleDeg: number, radius: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    };
  };

  const dataPoints = radarData.map((d) => getPoint(d.angle, (d.score / 100) * r));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <svg viewBox="0 0 140 120" className="w-full h-full">
      {/* 同心网格 */}
      {levels.map((level) => {
        const pts = radarData.map((d) => getPoint(d.angle, r * level));
        const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
        return (
          <path
            key={level}
            d={path}
            fill="none"
            stroke="oklch(var(--primary-200))"
            strokeWidth="0.5"
            opacity={0.5}
          />
        );
      })}

      {/* 轴线 */}
      {radarData.map((d) => {
        const p = getPoint(d.angle, r);
        return <line key={d.label} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="oklch(var(--primary-200))" strokeWidth="0.5" opacity={0.4} />;
      })}

      {/* 数据填充 */}
      <path d={dataPath} fill="oklch(var(--primary-400) / 0.15)" stroke="oklch(var(--primary-500))" strokeWidth="1.2" />

      {/* 数据点 */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="oklch(var(--primary-500))" />
      ))}

      {/* 标签 */}
      {radarData.map((d) => {
        const p = getPoint(d.angle, r + 14);
        const anchor = d.angle > 90 && d.angle < 270 ? 'end' : d.angle === 90 || d.angle === 270 ? 'middle' : 'start';
        return (
          <text
            key={d.label}
            x={p.x}
            y={p.y}
            textAnchor={anchor as 'start' | 'middle' | 'end'}
            fontSize="6"
            fill="oklch(var(--foreground-600))"
            fontFamily="var(--font-body)"
          >
            {d.short}
          </text>
        );
      })}

      {/* 分数标签 */}
      {radarData.map((d) => {
        const p = getPoint(d.angle, (d.score / 100) * r + 6);
        return (
          <text
            key={`score-${d.label}`}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            fontSize="6"
            fontWeight="bold"
            fill="oklch(var(--primary-600))"
            fontFamily="var(--font-mono)"
          >
            {d.score}
          </text>
        );
      })}

      {/* 中心总分 */}
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="7" fontWeight="bold" fill="oklch(var(--primary-700))" fontFamily="var(--font-heading)">
        85
      </text>
      <text x={cx} y={cy + 6} textAnchor="middle" fontSize="5" fill="oklch(var(--foreground-400))" fontFamily="var(--font-body)">
        /100
      </text>
    </svg>
  );
}

function RoleDecor({ type }: { type: string }) {
  if (type === 'chart') {
    return (
      <div className="flex items-center gap-1">
        <div className="flex items-end gap-px h-5">
          <div className="w-1.5 h-3 bg-primary-300 rounded-sm" />
          <div className="w-1.5 h-5 bg-primary-400 rounded-sm" />
          <div className="w-1.5 h-4 bg-primary-300 rounded-sm" />
          <div className="w-1.5 h-2 bg-primary-200 rounded-sm" />
        </div>
        <div className="w-7 h-5 rounded bg-primary-50 border border-primary-200/40 flex items-center justify-center">
          <i className="ri-map-pin-line text-[8px] text-primary-400" />
        </div>
      </div>
    );
  }
  if (type === 'checklist') {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded bg-gold-400 flex items-center justify-center">
            <i className="ri-check-line text-[7px] text-white" />
          </div>
          <div className="w-6 h-1 rounded bg-gold-200/60" />
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded bg-gold-400 flex items-center justify-center">
            <i className="ri-check-line text-[7px] text-white" />
          </div>
          <div className="w-5 h-1 rounded bg-gold-200/60" />
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded bg-gold-400 flex items-center justify-center">
            <i className="ri-check-line text-[7px] text-white" />
          </div>
          <div className="w-4 h-1 rounded bg-gold-200/60" />
        </div>
      </div>
    );
  }
  if (type === 'progress') {
    return (
      <div className="flex flex-col gap-1 justify-center">
        <div className="w-10 h-1.5 rounded-full bg-secondary-200/60 overflow-hidden">
          <div className="h-full w-[75%] rounded-full bg-secondary-400" />
        </div>
        <div className="w-10 h-1.5 rounded-full bg-secondary-200/60 overflow-hidden">
          <div className="h-full w-[60%] rounded-full bg-secondary-400" />
        </div>
        <div className="w-10 h-1.5 rounded-full bg-secondary-200/60 overflow-hidden">
          <div className="h-full w-[85%] rounded-full bg-secondary-400" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <div className="w-6 h-6 rounded bg-primary-50 border border-primary-200/40 flex items-center justify-center">
        <i className="ri-book-open-line text-[10px] text-primary-400" />
      </div>
    </div>
  );
}

export default function Section5Flow({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [activeTab, setActiveTab] = useState('视频片段');

  useEffect(() => {
    if (!titleVisible) { setVisibleSteps(0); return; }
    let step = 0;
    const t = setInterval(() => {
      step += 1;
      setVisibleSteps(step);
      if (step >= flowSteps.length) clearInterval(t);
    }, 200);
    return () => clearInterval(t);
  }, [titleVisible]);

  const ev = evidenceData[activeTab];

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-8">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-5 md:mb-6">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50/80 border border-primary-200/40 text-xs font-body font-semibold text-primary-600 tracking-[0.1em] mb-3 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <i className="ri-node-tree text-sm" />
            AI评课流程
          </span>
          <h2
            className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            从理解课堂到形成评价结果
          </h2>
          <p
            className={`text-sm text-foreground-500 font-body max-w-xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            AI评课不是把一节课压缩成一句评语，而是"结果+证据"同时出现
          </p>
        </div>

        {/* ── 顶部：7步流程 ── */}
        <div className="mb-5 md:mb-6">
          {/* 桌面端 */}
          <div className="hidden lg:flex items-start justify-between relative">
            {flowSteps.map((step, i) => {
              const isGold = step.color === 'accent';
              const isHighlighted = step.id === '06';
              return (
                <div
                  key={step.id}
                  className={`relative flex flex-col items-center text-center flex-1 mx-[6px] first:ml-0 last:mr-0 transition-all duration-500 ${
                    visibleSteps > i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  {/* 上方编号圆 */}
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center mb-2 text-xs font-bold font-mono text-white shadow-sm ${
                      isGold ? 'bg-gold-400' : 'bg-primary-400'
                    }`}
                  >
                    {step.id}
                  </div>

                  {/* 卡片 */}
                  <div
                    className={`w-full rounded-xl px-3 py-3 pt-4 pb-3 transition-all duration-400 cursor-default hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.04)] ${
                      isHighlighted
                        ? 'bg-gold-50/70 border border-gold-200/60'
                        : 'bg-white/80 border border-background-200/60 hover:border-primary-200/50'
                    }`}
                  >
                    {/* 图标 */}
                    <div
                      className={`w-11 h-11 rounded-2xl flex items-center justify-center mx-auto mb-2.5 ${
                        isGold
                          ? 'bg-gold-400/15'
                          : 'bg-primary-400/15'
                      }`}
                    >
                      <i className={`${step.icon} text-xl ${isGold ? 'text-gold-500' : 'text-primary-500'}`} />
                    </div>
                    {/* 标题 */}
                    <h4 className="font-body text-xs font-bold text-foreground-800 mb-1 leading-tight">{step.title}</h4>
                    {/* 描述 */}
                    <p className="text-[10px] text-foreground-500 font-body leading-relaxed whitespace-pre-line">{step.desc}</p>
                  </div>

                  {/* 下方虚线 + 节点 */}
                  <div className="flex flex-col items-center mt-1.5">
                    <div className="w-px h-3 border-l border-dashed border-primary-300/50" />
                    <div className={`w-2 h-2 rounded-full mt-0.5 ${isGold ? 'bg-gold-400' : 'bg-primary-400'}`} />
                  </div>

                  {/* 箭头 → 在卡片之间 */}
                  {i < flowSteps.length - 1 && (
                    <div className="absolute top-[76px] -right-[14px] z-10 flex items-center">
                      <i className="ri-arrow-right-line text-primary-400 text-base" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 移动端 */}
          <div className="lg:hidden grid grid-cols-3 sm:grid-cols-4 gap-2">
            {flowSteps.map((step, i) => {
              const isGold = step.color === 'accent';
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center text-center p-2 rounded-xl transition-all duration-500 ${
                    visibleSteps > i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  } ${isGold ? 'bg-gold-50/50 border border-gold-200/40' : 'bg-background-100/70 border border-primary-200/40'}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${isGold ? 'bg-gold-400' : 'bg-primary-400'}`}>
                    <i className={`${step.icon} text-sm text-white`} />
                  </div>
                  <span className="text-[9px] font-bold text-foreground-600 font-mono">{step.id}</span>
                  <h4 className="text-[10px] font-bold text-foreground-800 leading-tight">{step.title}</h4>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── 底部：三栏面板 ── */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 transition-all duration-1000 delay-500 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 左栏：评价结果示例 */}
          <div className="lg:col-span-5">
            <div className="rounded-xl overflow-hidden bg-white/60 border border-background-200/60 h-full">
              {/* 头部 */}
              <div className="px-4 py-2.5 border-b border-background-200/40 flex items-center gap-2">
                <i className="ri-trophy-line text-primary-500 text-sm" />
                <span className="text-sm font-body font-bold text-foreground-800">评价结果示例</span>
              </div>
              <div className="p-3 space-y-3">
                {/* 总分 + 评级 */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 p-3 rounded-lg bg-primary-50/60 border border-primary-200/30 text-center">
                    <span className="text-[10px] text-foreground-500 font-body">综合总分</span>
                    <div className="font-heading text-2xl font-bold text-primary-700 leading-tight">
                      85<span className="text-sm text-foreground-400 font-normal">/100</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 rounded-lg bg-gold-500 text-center min-w-[72px]">
                    <div className="text-base font-body font-bold text-white">优秀</div>
                    <div className="flex justify-center gap-0.5 mt-1">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className="ri-star-fill text-[8px] text-yellow-200" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* 雷达图 */}
                <div className="flex items-center gap-3">
                  <div className="w-32 h-28 flex-shrink-0">
                    <RadarChart />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <div className="text-[10px] font-body text-foreground-500 mb-1">分档结果</div>
                    <div className="flex gap-1">
                      {['优秀', '良好', '合格', '需重点支持'].map((g, gi) => (
                        <div
                          key={g}
                          className={`flex-1 py-1 rounded text-center text-[10px] font-body font-medium ${
                            gi === 0 ? 'bg-primary-500 text-white' : 'bg-background-100/80 text-foreground-500 border border-background-200/40'
                          }`}
                        >
                          {g}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 维度分数 */}
                <div className="grid grid-cols-5 gap-1.5">
                  {radarScores.map((s) => (
                    <div key={s.label} className="text-center py-1.5 rounded-lg bg-background-100/60 border border-background-200/30">
                      <div className="number-font text-sm font-bold text-primary-600">{s.score}</div>
                      <div className="text-[9px] text-foreground-500 font-body leading-tight">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 中栏：不同对象看不同内容 */}
          <div className="lg:col-span-4">
            <div className="rounded-xl overflow-hidden bg-white/60 border border-background-200/60 h-full">
              <div className="px-4 py-2.5 border-b border-background-200/40">
                <span className="text-sm font-body font-bold text-foreground-800">不同对象看不同内容</span>
              </div>
              <div className="p-3 space-y-2">
                {viewerRoles.map((v) => (
                  <div
                    key={v.role}
                    className="flex items-center gap-2.5 p-2.5 rounded-lg bg-background-50/80 border border-background-200/40 hover:border-primary-200/40 hover:-translate-y-0.5 hover:shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-all duration-300 cursor-default"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        v.color === 'primary'
                          ? 'bg-primary-50 border border-primary-200/40'
                          : v.color === 'accent'
                            ? 'bg-gold-50 border border-gold-200/40'
                            : 'bg-secondary-50 border border-secondary-200/40'
                      }`}
                    >
                      <i
                        className={`${v.icon} text-sm ${
                          v.color === 'primary' ? 'text-primary-500' : v.color === 'accent' ? 'text-gold-500' : 'text-secondary-500'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-body font-bold text-foreground-800">{v.role}</div>
                      <p className="text-[10px] text-foreground-500 font-body leading-relaxed whitespace-pre-line">{v.desc}</p>
                    </div>
                    <RoleDecor type={v.decor} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 右栏：可回溯证据 */}
          <div className="lg:col-span-3">
            <div className="rounded-xl overflow-hidden bg-white/60 border border-background-200/60 h-full">
              <div className="px-4 py-2.5 border-b border-background-200/40 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-link-unlink-m text-primary-500 text-sm" />
                  <span className="text-sm font-body font-bold text-foreground-800">可回溯证据</span>
                </div>
                <span className="text-[10px] text-foreground-400 font-body">点击查看</span>
              </div>
              <div className="p-3">
                {/* 标签页 */}
                <div className="flex flex-wrap gap-1 mb-2.5">
                  {evidenceTabs.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-2 py-0.5 rounded text-[10px] font-body font-medium transition-all cursor-pointer ${
                        activeTab === tab
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100/80 text-foreground-500 border border-background-200/40 hover:border-primary-200/40'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* 视频缩略图 */}
                <div className="relative rounded-lg overflow-hidden mb-2.5 aspect-video bg-background-200/50">
                  <img
                    src="https://readdy.ai/api/search-image?query=Chinese%20university%20classroom%20lecture%20scene%20with%20teacher%20and%20students%20seated%2C%20projector%20screen%20showing%20Chinese%20political%20text%2C%20clean%20modern%20interior%2C%20soft%20natural%20lighting%2C%20blue%20and%20white%20professional%20atmosphere%2C%20high%20quality%20photography&width=320&height=180&seq=p5-classroom-01&orientation=landscape"
                    alt="课堂视频片段"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                      <i className="ri-play-fill text-primary-600 text-sm" />
                    </div>
                  </div>
                  <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/50 text-[9px] text-white font-mono">
                    08:35 / 15:22
                  </div>
                </div>

                {/* 引用 */}
                <div className="flex gap-1.5 mb-2">
                  <i className="ri-double-quotes-l text-primary-300/50 text-sm flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground-700 font-body leading-relaxed">{ev.text}</p>
                </div>

                {/* 时间 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-foreground-400 font-mono">{ev.time}</span>
                  <span className="text-[10px] text-foreground-400">{ev.type}</span>
                </div>

                {/* 标签 */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {ev.tags.map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-body bg-primary-50 text-primary-600 border border-primary-200/40">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* 改进建议 */}
                <div className="flex items-start gap-2 p-2 rounded-lg bg-gold-50/40 border border-gold-200/30">
                  <i className="ri-lightbulb-line text-gold-500 text-xs flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-foreground-600 font-body leading-relaxed">
                    可进一步结合具体案例，深化学生对"人民至上"内涵的理解与认同。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}